"use client"

import { TimelineItem } from "@/types/types"
import { useCallback, useEffect, useRef, useState } from "react"
import { SwiperClass } from "swiper/react"

export const useAboutUs = (items: TimelineItem[]) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const years = items.map(item => item.year)

  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < items.length) {
        setCurrentIndex(index)
      }
    },
    [items.length],
  )

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % items.length)
  }, [items.length])

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + items.length) % items.length)
  }, [items.length])

  const goToYear = useCallback(
    (year: string) => {
      const index = years.indexOf(year)
      if (index !== -1) {
        setCurrentIndex(index)
      }
    },
    [years],
  )

  // Luôn cho phép điều hướng để có thể loop (quay lại đầu khi đến cuối)
  const canGoPrev = true
  const canGoNext = true

  return {
    currentIndex,
    years,
    goToSlide,
    goToNext,
    goToPrev,
    goToYear,
    canGoPrev,
    canGoNext,
    setCurrentIndex,
  }
}

export const useTimelineNavigation = (
  years: string[],
  activeIndex: number,
  onYearClick: (index: number) => void,
  canGoPrev: boolean,
  canGoNext: boolean,
) => {
  const swiperRef = useRef<SwiperClass | null>(null)
  const isProgrammaticChange = useRef(false)
  const [dragging, setDragging] = useState(false)
  const dragStartXRef = useRef(0)
  const dragStartTranslateRef = useRef(0)
  const currentTranslateRef = useRef(0)
  const dragButtonRef = useRef<HTMLButtonElement | null>(null)
  const swiperContainerRef = useRef<HTMLDivElement | null>(null)
  const clickedIndexRef = useRef<number | null>(null)

  const slidesPerView = 3
  const loopAdditionalSlides = 2
  // Enable Swiper loop khi >= 3 items
  // Nếu < 3: không loop
  // Nếu >= 3: dùng Swiper loop
  const enableLoop = years.length >= 3
  const displayYears = years

  // Sync activeIndex với swiper - đảm bảo item active luôn ở vị trí đầu tiên
  useEffect(() => {
    if (swiperRef.current && !isProgrammaticChange.current && years.length > 0) {
      isProgrammaticChange.current = true
      if (enableLoop) {
        // Với loop, slide tới vị trí activeIndex và đảm bảo nó là slide đầu tiên trong view
        swiperRef.current.slideToLoop(activeIndex, 900)
      } else {
        // Không loop: chỉ slide tới vị trí activeIndex
        // Đảm bảo activeIndex không vượt quá giới hạn
        const targetIndex = Math.min(activeIndex, years.length - 1)
        swiperRef.current.slideTo(targetIndex, 900)
      }
      setTimeout(() => {
        isProgrammaticChange.current = false
      }, 1000)
    }
  }, [activeIndex, years.length, enableLoop])

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      if (isProgrammaticChange.current) return

      // Lấy index của slide đầu tiên trong view (slide active)
      // Với slidesPerView = 3, slide đầu tiên trong view là slide active
      let actualIndex: number
      if (enableLoop) {
        // Với loop, realIndex là index thực tế của slide active trong mảng gốc
        // Đây chính là slide đầu tiên trong view
        // Ưu tiên sử dụng realIndex vì nó chính xác hơn với loop
        const realIdx = swiper.realIndex
        if (realIdx !== null && realIdx !== undefined && realIdx >= 0) {
          actualIndex = realIdx
        } else {
          // Fallback: tính từ activeIndex của Swiper
          // Với loop, activeIndex có thể là index trong loop, cần tính lại
          actualIndex = swiper.activeIndex % years.length
        }
        // Đảm bảo index trong phạm vi hợp lệ [0, years.length - 1]
        actualIndex = ((actualIndex % years.length) + years.length) % years.length
      } else {
        // Không loop: activeIndex là index của slide đầu tiên trong view
        actualIndex = Math.min(swiper.activeIndex, years.length - 1)
      }

      // Cập nhật activeIndex thành slide đầu tiên trong view
      // Luôn cập nhật để đảm bảo slide đầu tiên luôn active
      // Đặc biệt quan trọng khi navigate bằng arrow left/right
      if (actualIndex >= 0 && actualIndex < years.length) {
        isProgrammaticChange.current = true
        onYearClick(actualIndex)
        // Reset flag sau khi animation hoàn thành (900ms animation + buffer)
        setTimeout(() => {
          isProgrammaticChange.current = false
        }, 1000)
      }
    },
    [enableLoop, years.length, onYearClick],
  )

  const handlePrevClick = useCallback(() => {
    if (swiperRef.current && canGoPrev) {
      // Đảm bảo không block handleSlideChange khi navigate
      isProgrammaticChange.current = false
      if (enableLoop) {
        // >= 3 items: dùng Swiper loop
        swiperRef.current.slidePrev()
        // Ngay lập tức sau khi navigate, lấy index của slide đầu tiên trong view và set active
        setTimeout(() => {
          if (swiperRef.current) {
            const swiper = swiperRef.current
            const realIdx = swiper.realIndex
            if (realIdx !== null && realIdx !== undefined && realIdx >= 0) {
              const actualIndex = ((realIdx % years.length) + years.length) % years.length
              if (actualIndex >= 0 && actualIndex < years.length) {
                isProgrammaticChange.current = true
                onYearClick(actualIndex)
                setTimeout(() => {
                  isProgrammaticChange.current = false
                }, 100)
              }
            }
          }
        }, 50)
      } else {
        // < 3 items: không loop, chỉ slide nếu có thể
        const currentIndex = swiperRef.current.activeIndex
        if (currentIndex > 0) {
          swiperRef.current.slidePrev()
          // Ngay lập tức sau khi navigate, lấy index của slide đầu tiên trong view và set active
          setTimeout(() => {
            if (swiperRef.current) {
              const newIndex = swiperRef.current.activeIndex
              if (newIndex >= 0 && newIndex < years.length) {
                isProgrammaticChange.current = true
                onYearClick(newIndex)
                setTimeout(() => {
                  isProgrammaticChange.current = false
                }, 100)
              }
            }
          }, 50)
        }
      }
    }
  }, [canGoPrev, enableLoop, years.length, onYearClick])

  const handleNextClick = useCallback(() => {
    if (swiperRef.current && canGoNext) {
      // Đảm bảo không block handleSlideChange khi navigate
      isProgrammaticChange.current = false
      if (enableLoop) {
        // >= 3 items: dùng Swiper loop
        swiperRef.current.slideNext()
        // Ngay lập tức sau khi navigate, lấy index của slide đầu tiên trong view và set active
        setTimeout(() => {
          if (swiperRef.current) {
            const swiper = swiperRef.current
            const realIdx = swiper.realIndex
            if (realIdx !== null && realIdx !== undefined && realIdx >= 0) {
              const actualIndex = ((realIdx % years.length) + years.length) % years.length
              if (actualIndex >= 0 && actualIndex < years.length) {
                isProgrammaticChange.current = true
                onYearClick(actualIndex)
                setTimeout(() => {
                  isProgrammaticChange.current = false
                }, 100)
              }
            }
          }
        }, 50)
      } else {
        // < 3 items: không loop, chỉ slide nếu có thể
        const currentIndex = swiperRef.current.activeIndex
        if (currentIndex < years.length - 1) {
          swiperRef.current.slideNext()
          // Ngay lập tức sau khi navigate, lấy index của slide đầu tiên trong view và set active
          setTimeout(() => {
            if (swiperRef.current) {
              const newIndex = swiperRef.current.activeIndex
              if (newIndex >= 0 && newIndex < years.length) {
                isProgrammaticChange.current = true
                onYearClick(newIndex)
                setTimeout(() => {
                  isProgrammaticChange.current = false
                }, 100)
              }
            }
          }, 50)
        }
      }
    }
  }, [canGoNext, enableLoop, years.length, onYearClick])

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault()
    e.stopPropagation()

    // Lưu index được click để sử dụng trong handleMouseUp
    clickedIndexRef.current = index

    setDragging(true)
    dragStartXRef.current = e.clientX

    if (swiperRef.current) {
      const swiper = swiperRef.current
      const currentTranslate = swiper.getTranslate()
      dragStartTranslateRef.current = currentTranslate
      currentTranslateRef.current = currentTranslate
    }
  }, [])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!swiperRef.current) return

    const deltaX = e.clientX - dragStartXRef.current
    const newTranslate = dragStartTranslateRef.current + deltaX
    currentTranslateRef.current = newTranslate

    swiperRef.current.setTranslate(newTranslate)
  }, [])

  const handleMouseUp = useCallback(() => {
    setDragging(false)

    if (swiperRef.current) {
      const swiper = swiperRef.current
      const threshold = 50
      const deltaX = currentTranslateRef.current - dragStartTranslateRef.current

      if (Math.abs(deltaX) > threshold) {
        // Có drag: xử lý như drag
        if (deltaX > 0 && canGoPrev) {
          // Drag sang phải (prev)
          if (enableLoop) {
            // >= 3 items: dùng Swiper loop
            swiper.slidePrev()
          } else {
            // < 3 items: không loop
            const currentIndex = swiper.activeIndex
            if (currentIndex > 0) {
              swiper.slidePrev()
            } else {
              swiper.setTranslate(dragStartTranslateRef.current)
              swiper.update()
            }
          }
        } else if (deltaX < 0 && canGoNext) {
          // Drag sang trái (next)
          if (enableLoop) {
            // >= 3 items: dùng Swiper loop
            swiper.slideNext()
          } else {
            // < 3 items: không loop
            const currentIndex = swiper.activeIndex
            if (currentIndex < years.length - 1) {
              swiper.slideNext()
            } else {
              swiper.setTranslate(dragStartTranslateRef.current)
              swiper.update()
            }
          }
        } else {
          swiper.setTranslate(dragStartTranslateRef.current)
          swiper.update()
        }
      } else {
        // Không có drag: đây là click trực tiếp vào item
        // Slide tới vị trí item được click với animation mượt mà
        // Item sẽ di chuyển từ từ về vị trí đầu tiên trong 3 items hiển thị
        if (clickedIndexRef.current !== null) {
          const clickedIndex = clickedIndexRef.current
          clickedIndexRef.current = null

          // Cập nhật activeIndex
          if (clickedIndex !== activeIndex) {
            onYearClick(clickedIndex)
            isProgrammaticChange.current = true

            // Slide tới vị trí item được click với animation mượt mà
            const animationDuration = 900 // Animation chậm, mượt mà
            if (enableLoop) {
              // Với loop, sử dụng slideToLoop với animation
              swiper.slideToLoop(clickedIndex, animationDuration)
            } else {
              // Không loop, sử dụng slideTo với animation
              const targetIndex = Math.min(clickedIndex, years.length - 1)
              swiper.slideTo(targetIndex, animationDuration)
            }

            // Reset flag sau khi animation hoàn thành
            setTimeout(() => {
              isProgrammaticChange.current = false
            }, animationDuration + 100)
          } else {
            // Nếu click vào item đã active, chỉ reset translate
            swiper.setTranslate(dragStartTranslateRef.current)
            swiper.update()
          }
        } else {
          // Fallback: reset translate
          swiper.setTranslate(dragStartTranslateRef.current)
          swiper.update()
        }
      }
    }
  }, [canGoPrev, canGoNext, enableLoop, years.length, activeIndex, onYearClick])

  // Drag event listeners
  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = "grabbing"
      document.body.style.userSelect = "none"

      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
      }
    }
  }, [dragging, handleMouseMove, handleMouseUp])

  const handleSwiperInit = useCallback(
    (swiper: SwiperClass) => {
      swiperRef.current = swiper
      if (years.length > 0) {
        setTimeout(() => {
          if (enableLoop) {
            swiper.slideToLoop(activeIndex, 0)
          } else {
            swiper.slideTo(activeIndex, 0)
          }
        }, 100)
      }
    },
    [years.length, enableLoop, activeIndex],
  )

  return {
    swiperRef,
    dragging,
    dragButtonRef,
    swiperContainerRef,
    enableLoop,
    displayYears,
    slidesPerView,
    loopAdditionalSlides,
    handleSlideChange,
    handlePrevClick,
    handleNextClick,
    handleMouseDown,
    handleSwiperInit,
  }
}
